import React, { useState, useEffect } from 'react';

const XSearchScheduleForm = ({ 
  schedule, 
  onSubmit, 
  onCancel,
  isSubmitting 
}) => {
  const [formData, setFormData] = useState({
    cadence_value: 1,
    cadence_unit: 'hours',
    start_time: '08:00',
    end_time: '17:00',
    all_day: false,
    days_of_week: [],
    is_active: true
  });

  useEffect(() => {
    if (schedule) {
      setFormData({
        cadence_value: schedule.cadence_value || 1,
        cadence_unit: schedule.cadence_unit || 'hours',
        start_time: schedule.start_time || '08:00',
        end_time: schedule.end_time || '17:00',
        all_day: schedule.all_day !== undefined ? schedule.all_day : false,
        days_of_week: schedule.days_of_week || [],
        is_active: schedule.is_active !== undefined ? schedule.is_active : true
      });
    }
  }, [schedule]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseInt(value) || 1;
    setFormData(prev => ({
      ...prev,
      [name]: Math.min(50, Math.max(1, numValue))
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const days = prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort();
      return { ...prev, days_of_week: days };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label fw-bold">Cadence</label>
        <div className="row g-2">
          <div className="col-md-6">
            <input
              type="number"
              className="form-control"
              name="cadence_value"
              value={formData.cadence_value}
              onChange={handleNumberChange}
              min="1"
              max="50"
              required
              placeholder="Number (1-50)"
            />
          </div>
          <div className="col-md-6">
            <select
              className="form-select"
              name="cadence_unit"
              value={formData.cadence_unit}
              onChange={handleChange}
              required
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
            </select>
          </div>
        </div>
        <small className="text-muted">
          How often to run the search
        </small>
      </div>

      <div className="mb-3">
        <label className="form-label fw-bold">Start Time (EST)</label>
        <input
          type="time"
          className="form-control"
          name="start_time"
          value={formData.start_time}
          onChange={handleChange}
          required
        />
        <small className="text-muted">
          Time when the schedule begins running each day
        </small>
      </div>

      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="all_day"
          name="all_day"
          checked={formData.all_day}
          onChange={handleChange}
        />
        <label className="form-check-label" htmlFor="all_day">
          Run all day (24/7 at cadence interval)
        </label>
        <br />
        <small className="text-muted">
          {formData.all_day 
            ? `Will run continuously every ${formData.cadence_value} ${formData.cadence_value === 1 ? formData.cadence_unit.slice(0, -1) : formData.cadence_unit}`
            : `Will run within the time window from ${formData.start_time} to ${formData.end_time} at the cadence interval`}
        </small>
      </div>

      {!formData.all_day && (
        <div className="mb-3">
          <label className="form-label fw-bold">End Time (EST)</label>
          <input
            type="time"
            className="form-control"
            name="end_time"
            value={formData.end_time}
            onChange={handleChange}
            required={!formData.all_day}
          />
          <small className="text-muted">
            Time when the schedule stops running each day (must be after start time)
          </small>
        </div>
      )}

      <div className="mb-3">
        <label className="form-label fw-bold">Days of Week</label>
        <div className="btn-group w-100" role="group">
          {dayNames.map((day, index) => (
            <React.Fragment key={index}>
              <input
                type="checkbox"
                className="btn-check"
                id={`day-${index}`}
                checked={formData.days_of_week.includes(index)}
                onChange={() => handleDayToggle(index)}
              />
              <label 
                className={`btn btn-outline-primary ${formData.days_of_week.includes(index) ? 'active' : ''}`}
                htmlFor={`day-${index}`}
              >
                {day}
              </label>
            </React.Fragment>
          ))}
        </div>
        <small className="text-muted">
          Leave all unchecked to run every day
        </small>
      </div>

      <div className="mb-3 form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="is_active"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <label className="form-check-label" htmlFor="is_active">
          Schedule is active
        </label>
      </div>

      <div className="d-flex gap-2 justify-content-end">
        <button 
          type="button" 
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            schedule ? 'Update Schedule' : 'Create Schedule'
          )}
        </button>
      </div>
    </form>
  );
};

export default XSearchScheduleForm;
